/**
 * Downscales/re-encodes an image File client-side (via canvas) before upload.
 * Next.js's on-demand image optimizer fetches the full source file before it
 * can resize it, and aborts on a timeout if that source is too large — a
 * multi-thousand-pixel, multi-MB camera/Photoshop export blows past that.
 * Keeping uploads under a sane size avoids that failure for every future post.
 */
const MAX_DIMENSION = 1920;
// Kept high on purpose: this only ever runs once per upload (no repeated
// re-compression), so there's no need to trade visible quality for extra
// size savings — the resize alone already does most of the work.
const JPEG_QUALITY = 0.92;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_UPLOAD_MB = 20;

/** Validate format + raw size *before* attempting to decode/compress —
 * canvas-decoding an arbitrarily huge or non-image file can hang the tab. */
export const validateImageFile = (file, { allowedTypes = ALLOWED_TYPES, maxSizeMB = MAX_UPLOAD_MB } = {}) => {
  if (!file) return { valid: false, error: "No file selected." };

  if (!allowedTypes.includes(file.type)) {
    const formats = allowedTypes.map((t) => t.split("/")[1].toUpperCase()).join(", ");
    return { valid: false, error: `Unsupported format. Please upload a ${formats} image.` };
  }

  if (file.size > maxSizeMB * 1024 * 1024) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
    return { valid: false, error: `Image is too large (${sizeMb}MB). Max allowed is ${maxSizeMB}MB.` };
  }

  return { valid: true, error: null };
};

export const compressImage = (file, { maxDimension = MAX_DIMENSION, quality = JPEG_QUALITY } = {}) => {
  if (!file || !file.type || !file.type.startsWith("image/") || file.type === "image/svg+xml") {
    return Promise.resolve(file);
  }

  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const { width, height } = img;
      const scale = Math.min(1, maxDimension / Math.max(width, height));

      // Already small enough — don't re-encode and lose quality for nothing.
      if (scale >= 1 && file.size <= 2 * 1024 * 1024) {
        resolve(file);
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const keepPng = file.type === "image/png";
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const name = keepPng
            ? file.name
            : file.name.replace(/\.(png|jpe?g|webp|gif)$/i, "") + ".jpg";
          resolve(new File([blob], name, { type: blob.type, lastModified: Date.now() }));
        },
        keepPng ? "image/png" : "image/jpeg",
        keepPng ? undefined : quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file); // fall back to the original — better an uncompressed upload than a broken one
    };

    img.src = objectUrl;
  });
};
